import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { MoneyFragment } from '@/graphql/saleor/api';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './FinanceCalculator.styles';
import { AgosText, messages } from './messages';
export interface AgosFinanceCalculatorProps {
  monthlyLoanTerm?: number;
  price: MoneyFragment;
  selectMonthlyLoanTerm?: (value: number) => void;
}

export const AgosFinanceCalculatorText = ({
  monthlyLoanTerm,
  price,
}: {
  monthlyLoanTerm: number;
  price: MoneyFragment;
}) => {
  const { formatPrice } = useRegionProvider();
  const { agosPaymentMax, agosPaymentMin } = useConfigProvider();
  const monthlyPayments = useMemo(() => {
    return +(price.amount / monthlyLoanTerm).toFixed(2);
  }, [monthlyLoanTerm]);

  return (
    <Typography
      data-testid='agosFinanceCalculatorText'
      marginTop='s150'
      scale='p3'
    >
      <span className='notranslate' translate='no'>
        {AgosText({
          maxAmount: formatPrice(
            {
              amount: agosPaymentMax,
              currency: price.currency,
            },
            {
              currencyDisplay: 'symbol',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true,
            },
          ),
          minAmount: formatPrice(
            {
              amount: agosPaymentMin,
              currency: price.currency,
            },
            {
              currencyDisplay: 'symbol',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true,
            },
          ),
          amount: formatPrice(price, {
            currencyDisplay: 'symbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true,
          }),
          months: monthlyLoanTerm,
          monthlyPayment: formatPrice(
            {
              amount: monthlyPayments,
              currency: price.currency,
            },
            {
              currencyDisplay: 'symbol',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true,
            },
          ),
          totalAmountPayable: formatPrice(
            {
              amount: monthlyPayments * monthlyLoanTerm,
              currency: price.currency,
            },
            {
              currencyDisplay: 'symbol',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true,
            },
          ),
        })}
      </span>
    </Typography>
  );
};

export const AgosFinanceCalculator = ({
  monthlyLoanTerm: _monthlyLoanTerm,
  price,
  selectMonthlyLoanTerm,
}: AgosFinanceCalculatorProps) => {
  const { formatPrice } = useRegionProvider();
  const { formatMessage } = useIntl();
  const [monthlyLoanTerm, setMontlyLoanTerm] = useState(_monthlyLoanTerm ?? 6);
  const months = [6, 12, 24, 36, 48, 60];

  const handleMontlyLoanTerm = (value: number) => {
    setMontlyLoanTerm(value);
    selectMonthlyLoanTerm && selectMonthlyLoanTerm(value);
  };

  const monthlyPayments = useMemo(() => {
    return +(price.amount / monthlyLoanTerm).toFixed(2);
  }, [monthlyLoanTerm]);

  return (
    <S.Container data-test-id='AgosFinanceCalculator-Container'>
      <Flex>
        {months.map((el) => (
          <S.TermButton
            key={el}
            data-testid={`agosFinanceCalculatorTerm${el}`}
            isMobile={false}
            isSelected={el === monthlyLoanTerm}
            onClick={() => handleMontlyLoanTerm(el)}
          >
            <Typography>{el}</Typography>
            <S.Month>{formatMessage(messages.months)}</S.Month>
          </S.TermButton>
        ))}
      </Flex>

      <S.MonthlyPaymentsBox
        data-test-id='agosMonthlyPaymentBox'
        marginBottom='s100'
        marginTop='s100'
      >
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.monthlyLoanTerm)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {/* Keep the number React-controlled so it updates under browser
                translation (Google Translate hijacks text nodes otherwise). */}
            <span className='notranslate' translate='no'>
              {monthlyLoanTerm}
            </span>{' '}
            {formatMessage(messages.months)}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.yourMonthlyPayments)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            <span className='notranslate' translate='no'>
              {formatPrice(
                {
                  amount: monthlyPayments,
                  currency: price.currency,
                },
                {
                  currencyDisplay: 'symbol',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  useGrouping: true,
                },
              )}
            </span>
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.taeg)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            0 %
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.totalPrice)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            <span className='notranslate' translate='no'>
              {formatPrice(
                {
                  amount: monthlyPayments * monthlyLoanTerm,
                  currency: price.currency,
                },
                {
                  currencyDisplay: 'symbol',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  useGrouping: true,
                },
              )}
            </span>
          </Typography>
        </Flex>
      </S.MonthlyPaymentsBox>
    </S.Container>
  );
};
